import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { CanActivate } from '@angular/router';
import { inject } from '@angular/core';

export interface SeoMetaConfig {
  path: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly seoConfigs: SeoMetaConfig[] = [
    {
      path: '',
      title: 'GradPilots',
      description: 'Welcome to GradPilots - Your one-stop platform for global admissions guidance, mentorship, and resources.',
      ogTitle: 'GradPilots',
      ogDescription: 'Welcome to GradPilots - Your one-stop platform for global admissions guidance, mentorship, and resources.',
      ogUrl: 'https://gradpilots.com/'
    },
    {
      path: '/our-mission',
      title: 'Our Mission | GradPilots',
      description: 'Learn about the mission and vision of GradPilots.',
      ogTitle: 'Our Mission | GradPilots',
      ogDescription: 'Learn about the mission and vision of GradPilots.',
      ogUrl: 'https://gradpilots.com/our-mission'
    },
    {
      path: '/our-mentors',
      title: 'Our Mentors | GradPilots',
      description: 'Meet our experienced mentors at GradPilots.',
      ogTitle: 'Our Mentors | GradPilots',
      ogDescription: 'Meet our experienced mentors at GradPilots.',
      ogUrl: 'https://gradpilots.com/our-mentors'
    },
    {
      path: '/contact-us',
      title: 'Contact Us | GradPilots',
      description: 'Get in touch with GradPilots for your queries and support.',
      ogTitle: 'Contact Us | GradPilots',
      ogDescription: 'Get in touch with GradPilots for your queries and support.',
      ogUrl: 'https://gradpilots.com/contact-us'
    },
    {
      path: '/find-a-mentor',
      title: 'Find a Mentor | GradPilots',
      description: 'Connect with mentors for your academic and career guidance.',
      ogTitle: 'Find a Mentor | GradPilots',
      ogDescription: 'Connect with mentors for your academic and career guidance.',
      ogUrl: 'https://gradpilots.com/find-a-mentor'
    },
    {
      path: '/gradpath',
      title: 'GradPath | GradPilots',
      description: 'Explore your academic journey with GradPath.',
      ogTitle: 'GradPath | GradPilots',
      ogDescription: 'Explore your academic journey with GradPath.',
      ogUrl: 'https://gradpilots.com/gradpath'
    },
    {
      path: '/our-services',
      title: 'Our Services | GradPilots',
      description: 'Discover the services offered by GradPilots.',
      ogTitle: 'Our Services | GradPilots',
      ogDescription: 'Discover the services offered by GradPilots.',
      ogUrl: 'https://gradpilots.com/our-services'
    },
    {
      path: '/post-admission-services',
      title: 'Post Admission Services | GradPilots',
      description: 'Support and services after your admission.',
      ogTitle: 'Post Admission Services | GradPilots',
      ogDescription: 'Support and services after your admission.',
      ogUrl: 'https://gradpilots.com/post-admission-services'
    },
    {
      path: '/visa-work-eligibility',
      title: 'Visa & Work Eligibility | GradPilots',
      description: 'Information on visa and work eligibility for students.',
      ogTitle: 'Visa & Work Eligibility | GradPilots',
      ogDescription: 'Information on visa and work eligibility for students.',
      ogUrl: 'https://gradpilots.com/visa-work-eligibility'
    },
    {
      path: '/scholarship-database',
      title: 'Scholarship Database | GradPilots',
      description: 'Find scholarships for your academic journey with GradPilots.',
      ogTitle: 'Scholarship Database | GradPilots',
      ogDescription: 'Find scholarships for your academic journey with GradPilots.',
      ogUrl: 'https://gradpilots.com/scholarship-database'
    },
    {
      path: '/admission-calendar',
      title: 'Admission Calendar | GradPilots',
      description: 'Stay updated with the latest admission dates and deadlines.',
      ogTitle: 'Admission Calendar | GradPilots',
      ogDescription: 'Stay updated with the latest admission dates and deadlines.',
      ogUrl: 'https://gradpilots.com/admission-calendar'
    },
    {
      path: '/college-rankings',
      title: 'College Rankings | GradPilots',
      description: 'Explore college rankings to make informed decisions.',
      ogTitle: 'College Rankings | GradPilots',
      ogDescription: 'Explore college rankings to make informed decisions.',
      ogUrl: 'https://gradpilots.com/college-rankings'
    }
  ];

  private renderer: Renderer2;

  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setSeoTags(path: string): void {
    // Normalize root path
    let cleanPath = path.split('?')[0].split('#')[0];
    if (cleanPath === '') cleanPath = '/';

    const config = this.seoConfigs.find(cfg => cfg.path === cleanPath);
    if (!config) return;

    this.title.setTitle(config.title);
    this.meta.updateTag({ name: 'description', content: config.description });
    if (config.ogTitle) {
      this.meta.updateTag(
        { property: 'og:title', content: config.ogTitle },
        "property='og:title'"
      );
    }
    if (config.ogDescription) {
      this.meta.updateTag(
        { property: 'og:description', content: config.ogDescription },
        "property='og:description'"
      );
    }
    if (config.ogUrl) {
      this.meta.updateTag(
        { property: 'og:url', content: config.ogUrl },
        "property='og:url'"
      );
    }

    // SSR-compatible canonical tag update
    if (config.ogUrl) {
      this.setCanonicalURL(config.ogUrl);
    }
  }

  setCanonicalURL(url: string): void {
    // Remove existing canonical link if present
    const existingLink = this.document.querySelector("link[rel='canonical']");
    if (existingLink) {
      this.renderer.removeChild(this.document.head, existingLink);
    }

    // Create new canonical link
    const link: HTMLLinkElement = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'canonical');
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.appendChild(this.document.head, link);
  }
}

